import {
  MembershipDependent,
  Membership,
  Member,
  MembershipDependentStatus,
  getMemberships,
  getMembershipDependents,
  submitMembershipDependents,
  updateMembershipDependents,
} from "@/services/core-api-adapter";
import { setup, assign, fromPromise } from "xstate";

interface Context {
  getDependentsResponse: MembershipDependent[] | [];
  activeDependent: MembershipDependent | null;
  getMembershipsResponse: Membership[] | null;
  submittingDependentDetailsError: any;
  maxAllowedDependents: number;
  maxDependentAge: number;
  mainMember: Member | null;
}

const initialContextValues: Context = {
  getDependentsResponse: [],
  activeDependent: null,
  getMembershipsResponse: null,
  submittingDependentDetailsError: null,
  maxAllowedDependents: 3,
  maxDependentAge: 18,
  mainMember: null,
};

export const eventNames = {
  ADD_PEOPLE_BUTTON_CLICKED: "ADD_PEOPLE_BUTTON_CLICKED",
  BACK_BUTTON_CLICKED: "BACK_BUTTON_CLICKED",
  CLOSE_BUTTON_CLICKED: "CLOSE_BUTTON_CLICKED",
  CONTINUE_BUTTON_CLICKED: "CONTINUE_BUTTON_CLICKED",
  EDIT_DEPENDENT_BUTTON_CLICKED: "EDIT_DEPENDENT_BUTTON_CLICKED",
  DELETE_DEPENDENT_BUTTON_CLICKED: "DELETE_DEPENDENT_BUTTON_CLICKED",
  CONFIRM_DELETE_DEPENDENT_BUTTON_CLICKED:
    "CONFIRM_DELETE_DEPENDENT_BUTTON_CLICKED",
};

export const myProfileFlowStateMachine = setup({
  types: {
    context: {} as Context,
    events: {} as any,
  },
  actions: {
    addDependentDetailsToContext: assign({
      activeDependent: ({ event }) => {
        return event.input;
      },
    }),
    addDependentsResponseToContext: assign({
      getDependentsResponse: ({ event }) => {
        return event.output;
      },
    }),
    updateDependentDetailsInContext: assign({
      activeDependent: ({ event }) => {
        return { ...event.input, status: MembershipDependentStatus.ACTIVE };
      },
    }),
    removeDependentDetailsInContext: assign({
      activeDependent: () => {
        return null;
      },
    }),
    addSubmittingDependentDetailsErrorsToContext: assign({
      submittingDependentDetailsError: ({ event }) => {
        return event.output;
      },
    }),
  },
  actors: {
    getMemberships: fromPromise(async () => {
      return await getMemberships();
    }),
    fetchingDependents: fromPromise(async () => {
      return await getMembershipDependents();
    }),
    submitDependentDetails: fromPromise(
      async ({
        input,
      }: {
        input: { activeDependent: MembershipDependent | null };
      }) => {
        return await submitMembershipDependents({
          dependents: input?.activeDependent ? [input.activeDependent] : [],
        });
      }
    ),
    updateMembershipDependents: fromPromise(
      async ({
        input,
      }: {
        input: { activeDependent: MembershipDependent | null };
      }) => {
        return await updateMembershipDependents({
          dependents: input?.activeDependent ? [input.activeDependent] : [],
        });
      }
    ),
    deleteDependent: fromPromise(
      async ({
        input,
      }: {
        input: { activeDependent: MembershipDependent | null };
      }) => {
        const activeDependent = {
          ...input?.activeDependent,
          status: MembershipDependentStatus.DELETE,
        };

        return await updateMembershipDependents({
          dependents: (activeDependent ? [activeDependent] : []) as any,
        });
      }
    ),
  },
}).createMachine({
  context: initialContextValues,
  id: "membershipAddDependentsFlow",
  initial: "fetchingDependents",
  states: {
    fetchingDependents: {
      entry: assign({ ...initialContextValues }),
      invoke: {
        id: "fetchingDependents",
        input: {},
        onDone: {
          target: "showingProfile",
          actions: {
            type: "addDependentsResponseToContext",
          },
        },
        onError: {
          target: "error",
        },
        src: "fetchingDependents",
      },
    },
    showingProfile: {
      on: {
        BACK_BUTTON_CLICKED: {
          target: "exit",
        },
        ADD_PEOPLE_BUTTON_CLICKED: {
          target: "collectingDependentDetails",
          actions: {
            type: "removeDependentDetailsInContext",
          },
        },
        EDIT_DEPENDENT_BUTTON_CLICKED: {
          target: "editingDependentDetails",
          actions: {
            type: "updateDependentDetailsInContext",
          },
        },
        DELETE_DEPENDENT_BUTTON_CLICKED: {
          target: "confirmingDependentRemoval",
          actions: {
            type: "updateDependentDetailsInContext",
          },
        },
      },
    },
    collectingDependentDetails: {
      on: {
        CONTINUE_BUTTON_CLICKED: {
          target: "submittingDependentDetails",
          actions: {
            type: "addDependentDetailsToContext",
          },
        },
        BACK_BUTTON_CLICKED: {
          target: "showingProfile",
        },
      },
    },
    fullscreenError: {
      on: {
        CONTINUE_BUTTON_CLICKED: {
          target: "fetchingDependents",
        },
      },
    },
    error: {
      type: "final",
    },
    submittingDependentDetails: {
      invoke: {
        id: "submittingDependentDetails",
        src: "submitDependentDetails",
        input: ({ context }) => {
          return { activeDependent: context.activeDependent };
        },
        onDone: {
          target: "thankYou",
        },
        onError: {
          target: "fullscreenError",
        },
      },
    },
    exit: {
      type: "final",
    },
    thankYou: {
      on: {
        CONTINUE_BUTTON_CLICKED: {
          target: "fetchingDependents",
        },
      },
    },
    editingDependentDetails: {
      on: {
        CONTINUE_BUTTON_CLICKED: {
          target: "updatingMembershipDependents",
          actions: {
            type: "updateDependentDetailsInContext",
          },
        },
        BACK_BUTTON_CLICKED: {
          target: "fetchingDependents",
        },
      },
    },
    confirmingDependentRemoval: {
      on: {
        CONFIRM_DELETE_DEPENDENT_BUTTON_CLICKED: {
          target: "deletingDependentDetails",
        },
        BACK_BUTTON_CLICKED: {
          target: "fetchingDependents",
        },
        CLOSE_BUTTON_CLICKED: {
          target: "fetchingDependents",
        },
      },
    },
    updatingMembershipDependents: {
      invoke: {
        id: "updatingMembershipDependents",
        input: ({ context }) => {
          return { activeDependent: context.activeDependent };
        },
        onDone: {
          target: "thankYou",
        },
        onError: {
          target: "fullscreenError",
          actions: {
            type: "addSubmittingDependentDetailsErrorsToContext",
          },
        },
        src: "updateMembershipDependents",
      },
    },
    deletingDependentDetails: {
      invoke: {
        id: "deletingDependentDetails",
        src: "deleteDependent",
        input: ({ context }) => {
          return { activeDependent: context.activeDependent };
        },
        onDone: {
          target: "deleteDependentSuccess",
        },
        onError: {
          target: "deleteDependentError",
        },
      },
    },
    deleteDependentSuccess: {
      on: {
        CLOSE_BUTTON_CLICKED: {
          target: "fetchingDependents",
        },
      },
    },
    deleteDependentError: {
      on: {
        CLOSE_BUTTON_CLICKED: {
          target: "fetchingDependents",
        },
      },
    },
  },
});
